import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { currentTenant } from '../config/tenant';
import storageService from './storageService';

class AppleAuthService {
  constructor() {
    this.initialized = false;
    this.initializing = false;
    this.initPromise = null;
  }

  getSocialLoginPlugin() {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Apple Sign-In is only available on native platforms');
    }

    const plugin = Capacitor.Plugins.SocialLogin;
    if (!plugin) {
      throw new Error('SocialLogin plugin not found in Capacitor.Plugins');
    }

    return plugin;
  }

  async initialize() {
    if (this.initialized) {
      console.log('Apple Login already initialized');
      return;
    }

    if (this.initializing && this.initPromise) {
      console.log('Apple Login initialization in progress, waiting...');
      return this.initPromise;
    }

    this.initializing = true;
    this.initPromise = this._doInitialize();

    try {
      await this.initPromise;
    } finally {
      this.initializing = false;
      this.initPromise = null;
    }
  }
  async _doInitialize() {
    try {
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();

      console.log('=== Apple Login Initialization ===');
      console.log('Platform:', platform);
      console.log('Is Native:', isNative);

      if (isNative && platform === 'ios') {
        const SocialLogin = this.getSocialLoginPlugin();
        await SocialLogin.initialize({
          apple: {
            useProperTokenExchange: true
          }
        });
        console.log('✓ Apple provider initialized successfully');
      } else {
        console.log('Apple Login notice: Platform is not iOS native');
      }

      this.initialized = true;
      console.log('=== Apple Initialization Complete ===');
    } catch (error) {
      console.error('✗ Failed to initialize Apple Login:', error);
      this.initialized = false;
      throw new Error('Failed to initialize Apple Login: ' + error.message);
    }
  }

  /**
   * Login with Apple (iOS native only)
   * @returns {Promise<Object>} Backend verification response containing token & user data
   */
  async login() {
    try {
      if (!this.initialized) {
        await this.initialize();
      }

      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
        throw new Error('Apple Sign-In is only available on iOS native devices');
      }

      const SocialLogin = this.getSocialLoginPlugin();

      console.log('Calling SocialLogin.login for Apple...');
      const response = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name']
        }
      });

      console.log('Apple login raw response:', response);

      if (response.provider !== 'apple') {
        throw new Error('Invalid provider response from SocialLogin plugin');
      }

      const result = response.result || {};
      const identityToken = result.identityToken || result.idToken || '';

      if (!identityToken) {
        throw new Error('Apple Sign-In failed: No identity token returned from native SDK');
      }

      // With useProperTokenExchange: true, result.authorizationCode contains the raw authorization code.
      // In legacy mode fallback: result.accessToken?.token contains the authorization code.
      const authorizationCode = result.authorizationCode || result.accessToken?.token || '';

      const referralCode = localStorage.getItem('campaign_referral_code') || null;

      const payload = {
        identityToken,
        givenName: result.givenName || result.profile?.givenName || '',
        familyName: result.familyName || result.profile?.familyName || '',
        authorizationCode,
        platform: 'ios',
        referral_code: referralCode
      };

      const backendResponse = await this.verifyWithBackend(payload);
      return backendResponse;

    } catch (error) {
      console.error('Apple login failed:', error);
      const rawMessage = error.message || '';
      const lower = rawMessage.toLowerCase();
      if (lower.includes('cancel') || lower.includes('1001') || lower.includes('1000')) {
        const cancelError = new Error('Apple Sign-In failed: Cancelled by user.');
        cancelError.code = 'CANCELED';
        throw cancelError;
      }
      throw error;
    }
  }

  /**
   * Verify Apple identity token with backend
   * @param {Object} payload - Complete request payload (identityToken, givenName, familyName, authorizationCode, platform)
   * @returns {Promise<Object>} User data and auth token
   */
  async verifyWithBackend(payload) {
    try {
      let apiHost = (Capacitor.isNativePlatform() && currentTenant.apiHost)
        ? currentTenant.apiHost
        : (process.env.VUE_APP_API_HOST || currentTenant.apiHost || 'https://app.mymembership.id');

      apiHost = apiHost.replace(/\/+$/, '');
      const apiUrl = `${apiHost}/api/auth/apple/verify-token`;

      console.log('Verifying Apple token with backend:', apiUrl);

      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Tenant-Domain': currentTenant.domain
        }
      });

      console.log('Apple Backend response:', response.data);

      if (response.data.status) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Apple verification failed');
      }

    } catch (error) {
      console.error('Apple backend verification failed details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });

      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      const authError = new Error(errorMessage);
      authError.code = errorMessage;
      authError.httpStatus = error.response?.status;
      throw authError;
    }
  }

  /**
   * Logout Apple user session
   */
  async logout() {
    try {
      if (Capacitor.isNativePlatform()) {
        const SocialLogin = this.getSocialLoginPlugin();
        try {
          await SocialLogin.logout({ provider: 'apple' });
        } catch (e) {
          // Ignore logout error if session was already invalidated or not initialized
        }
      }
      await storageService.clearAuthData();
      console.log('Logged out Apple user successfully');
    } catch (error) {
      console.error('Apple logout failed:', error);
      throw error;
    }
  }

  async isLoggedIn() {
    return await storageService.isLoggedIn();
  }

  async getAuthToken() {
    return await storageService.getItem('auth_token');
  }

  async getUserData() {
    return await storageService.getItemJSON('user_data');
  }
}

export default new AppleAuthService();
